/**
 * Team & Role types for producer members and invitations
 */

export type ProducerRole = 'admin' | 'editor' | 'staff' | 'viewer';

export type MemberStatus = 'active' | 'invited' | 'expired';

export interface ProducerMember {
  id: string;
  producerId: string;
  userId?: string;
  email: string;
  name?: string;
  /** Public title shown on producer page */
  title?: string;
  avatarUrl?: string;
  role: ProducerRole;
  status: MemberStatus;
  /** Whether this member should be shown on public producer page */
  isPublic?: boolean;
  /** Bio for public profile */
  bio?: string;
  invitedAt?: string;
  createdAt?: string;
  onboardedAt?: string | null;
}

export interface ProducerInvitation {
  id: string;
  producerId: string;
  email: string;
  role: ProducerRole;
  status: Exclude<MemberStatus, 'active'>;
  invitedAt: string;
}

export interface InviteMemberPayload {
  email: string;
  role: ProducerRole;
  isPublic?: boolean;
  publicName?: string;
  title?: string;
  bio?: string;
}

export interface UpdateMemberPayload {
  role?: ProducerRole;
  isPublic?: boolean;
  title?: string;
  bio?: string;
  avatarUrl?: string;
}

/** Role display labels (Swedish) — UI-facing names per Värdteam v1 spec */
export const ROLE_LABELS: Record<ProducerRole, string> = {
  admin: 'Ägare / Medvärd',
  editor: 'Redaktör',
  staff: 'Personal',   // Hidden in UI but kept for DB compatibility
  viewer: 'Läsare',
};

/** Roles visible in team UI (staff hidden per spec) */
export const VISIBLE_ROLES: ProducerRole[] = ['admin', 'editor', 'viewer'];

/** Roles available for invite/change */
export const ASSIGNABLE_ROLES: ProducerRole[] = ['admin', 'editor', 'viewer'];

/** Role descriptions for help text */
export const ROLE_DESCRIPTIONS: Record<ProducerRole, string> = {
  admin: 'Full åtkomst till redigering, publicering och teamhantering.',
  editor: 'Kan redigera gårdsinformation, bilder och aktiviteter.',
  staff: 'Kan hantera produkter och ordrar.',
  viewer: 'Kan se information men inte ändra något.',
};

/** Role permissions matrix */
export const ROLE_PERMISSIONS: Record<ProducerRole, {
  grundinfo: 'full' | 'view' | 'none';
  products: 'full' | 'view' | 'none';
  orders: 'full' | 'view' | 'none';
  publishing: 'full' | 'view' | 'none';
  team: 'full' | 'view' | 'none';
}> = {
  admin: {
    grundinfo: 'full',
    products: 'full',
    orders: 'full',
    publishing: 'full',
    team: 'full',
  },
  editor: {
    grundinfo: 'full',
    products: 'full',
    orders: 'view',
    publishing: 'none',
    team: 'none',
  },
  staff: {
    grundinfo: 'view',
    products: 'full',
    orders: 'full',
    publishing: 'none',
    team: 'none',
  },
  viewer: {
    grundinfo: 'view',
    products: 'view',
    orders: 'view',
    publishing: 'none',
    team: 'none',
  },
};

/**
 * Check if a given caller role can manage (change role / remove) a target role.
 */
export function canManageRole(callerRole: ProducerRole, targetRole: ProducerRole): boolean {
  if (callerRole === 'admin') {
    return true; // Admin can manage all roles; last-admin guard is enforced server-side
  }
  return false;
}

/**
 * Check if a member is the last admin in the team.
 * Used to show warnings and disable destructive actions in UI.
 */
export function isLastAdmin(members: Pick<ProducerMember, 'role'>[], targetRole: ProducerRole): boolean {
  if (targetRole !== 'admin') return false;
  const adminCount = members.filter((m) => m.role === 'admin').length;
  return adminCount <= 1;
}
